import React, {useEffect, useState} from "react";

import {Box} from '@mui/material';

import "../App.css"
import {useAppCtx} from "../app/AppCtx";
import {useCrewCtx} from "../app/CrewCtx";
import {PageHeader} from "./PageHeader";
import {CxGrid} from "../cmx/CxGrid";
import {
    CxCancelSaveButton,
    CxCloseButton,
    CxMetaInfo,
    CxAutocomplete,
    CxDeleteCancelSaveButton
} from "../cmx/CxForm";
import {CxConfirmDialog} from "../cmx/CxDialog";
import {CxFormFeedback, CxSnack} from "../cmx/CxFeedback";
import {Team} from "../domain/Team";
import {initialCall, runningCall} from "../gateway/CallService";
import {teamSave, teamDelete} from "../gateway/TeamService";
import {ValidationUtil} from "../lib/ValidationUtil";

// --------------------------------------------------------------------------------------------------------------------
// RenderCrew
// --------------------------------------------------------------------------------------------------------------------

const RenderTeam = (readOnly, currentData=null) => {
    const isUpdate = currentData !== null;

    const {handleAction, currentReturnTo} = useAppCtx();
    const {crew, loadCrews, lazyloadCrews} = useCrewCtx();

    const [formValues, setFormValues] = useState(isUpdate ? Team.mapDbToForm(currentData) : Team.getFormValues());
    const [formErrors, setFormErrors] = useState(Team.getFormErrors());
    const [openConfirm, setOpenConfirm] = useState(false);
    const [call, setCall] = useState(initialCall())
    const [snack, setSnack] = useState(null);

    useEffect(() => {
        lazyloadCrews();
    }, []);

    //
    // handlers
    //
    const handleTeamLeadChange = (event, item) => {
        // important: keep item null part 'as-is', do not change logic!
        if (!item) {
            setFormValues((prevValues) => ({
                ...prevValues,
                lead_crew_id: null,
            }));
        } else {
            const leadId = item.value;
            const memberCrewIds = formValues['member_crew_ids'];
            if ( Team.isLeadInMembers(memberCrewIds,leadId) ) {
                // if new lead is already a group member, remove it from group
                const memberIds = Team.filterMembers(memberCrewIds, leadId);
                setFormValues((prevValues) => ({
                    ...prevValues,
                    lead_crew_id: leadId,
                    member_crew_ids: memberIds
                }));
                // add snack message
                const name = crew.getName(leadId);
                const snack= `${name} von Gruppenmitglied zu Gruppe verschoben.`;
                setSnack(snack)
            } else {
                setFormValues((prevValues) => ({
                    ...prevValues,
                    lead_crew_id: leadId
                }));
            }
        }
        setFormErrors((prev) => ({ ...prev, lead_crew_id: false }));
    };

    const handleTeamMemberChange = (event, item) => {
        // item has objects like [{id: ..., label:...}, {value: ..., label:...}]
        let memberCrewIds = item.map(i => i.id ?? i.value);
        memberCrewIds = Team.filterUniqueMembers(memberCrewIds);

        // check if teamLeadCrewId is part of the members and remove the member entry
        const leadId = formValues['lead_crew_id'];
        const isLeadInMembers = Team.isLeadInMembers(memberCrewIds,leadId);
        const memberIds = isLeadInMembers ? Team.filterMembers(memberCrewIds, leadId) : memberCrewIds;
        if (isLeadInMembers) {
            const name = crew.getName(leadId);
            const snack= `${name} ist bereits in Gruppe erfasst.`;
            setSnack(snack)
        }

        // update form values
        setFormValues((prevValues) => ({
            ...prevValues,
            member_crew_ids: memberIds
        }));
        setFormErrors((prev) => ({ ...prev, member_crew_ids: false }));
    };

    const handleSubmit = async (action) => {
        if (action === "delete") {
            setOpenConfirm(true)
            return
        }

        if (action === "save") {
            // validate
            const errorsCopy = { ...formErrors }; // Create a copy of the errors object
            const isValid = validate(formValues, errorsCopy);
            if (!isValid) {
                setFormErrors(errorsCopy); // Update errors in state
                return
            }
            // save (create or update)
            setCall(runningCall);
            const responseCall = await teamSave(Team.mapFormToDb(formValues));
            if (responseCall.isError) {
                setCall(responseCall)
                return
            } else {
                loadCrews();
            }
        }

        // return (after cancel, close or successfull save action)
        const returnTo = currentReturnTo ? currentReturnTo : 'team';
        handleAction(returnTo)
    };


    const handleConfirm = async (action) => {
        setOpenConfirm(false);
        if (action === "yes") {
            setCall(runningCall);
            const responseCall = await teamDelete(currentData.id);
            if (responseCall.isError) {
                setCall(responseCall)
            } else {
                loadCrews();
                const returnTo = currentReturnTo ? currentReturnTo : 'team';
                handleAction(returnTo)
            }
        }
    }

    const handleCloseSnack = () => {
        setSnack(null);
    };

    //
    // validation
    //
    function validate(formValues, formErrors) {
        let isValid = true;

        // remove leading and trailing blanks on all input fields
        ValidationUtil.trimTextFields(formValues);

        // validate required fields
        isValid = ValidationUtil.required(formValues, formErrors, Team.getFormRequiredFields());

        // return final validation status
        return isValid;
    }

    //
    // form inputs
    //
    const formTeamLeadSelection = (readOnly) => {
        let items = crew.getTeamLeadWorkerSelection();
        let current_id = formValues['lead_crew_id'];
        let currentValue = items.find(item => item.value === current_id) || null;

        return <CxAutocomplete key="team_lead" label="Gruppe"
                               currentValue={currentValue}
                               items={items}
                               onChange={handleTeamLeadChange}
                               error={formErrors['lead_crew_id']}
                               readOnly={readOnly} />
    }

    const formTeamMemberSelection = (readOnly) => {
        let items = crew.getTeamLeadWorkerSelection();
        let currentValues = crew.getTeamMemberItems(formValues['member_crew_ids']);

        return <CxAutocomplete key="team_members" label="Gruppemitglieder"
                               currentValue={currentValues}
                               items={items}
                               onChange={handleTeamMemberChange}
                               error={formErrors['member_crew_ids']}
                               readOnly={readOnly}
                               multiple={true} />
    }

    const formCloseButton = () => {
        return <CxCloseButton handleSubmit={handleSubmit} />
    }

    const formSaveButton = () => {
        return <CxCancelSaveButton handleSubmit={handleSubmit} />
    }

    const formUpdateButtons = () => {
        return <CxDeleteCancelSaveButton  handleSubmit={handleSubmit} />
    }

    //
    // return form content
    //
    return (
        <>
            <CxFormFeedback call={call} />

            <CxGrid size="lg">{ formTeamLeadSelection(readOnly) }</CxGrid>
            <CxGrid size="lg" />

            <CxGrid size="lg">{ formTeamMemberSelection(readOnly) }</CxGrid>
            <CxGrid size="lg" />

            <CxGrid size="lg" justifyContent="flex-end">
                {readOnly ? formCloseButton() : isUpdate ? formUpdateButtons() : formSaveButton()}
            </CxGrid>

            <CxMetaInfo values={formValues} />

            <CxConfirmDialog
                open={openConfirm}
                title="Gruppe löschen"
                text="Soll die Gruppe gelöscht werden?"
                handleConfirm={handleConfirm}
                severity="Delete" />

            <CxSnack
                message={snack}
                handleClose={handleCloseSnack} />
        </>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// Create, Update and Delete
// --------------------------------------------------------------------------------------------------------------------

export function TeamCreate() {
    const title = "Gruppe erstellen";

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                <PageHeader title={title} />
                { RenderTeam(false) }
            </CxGrid>
        </Box>
    );
}

export function TeamUpdate() {
    const { currentData } = useAppCtx();
    const title = "Gruppe bearbeiten";

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                <PageHeader title={title} />
                { RenderTeam(false, currentData) }
            </CxGrid>
        </Box>
    );
}

export function TeamShow() {
    const { currentData } = useAppCtx();
    const title = "Gruppe anzeigen";

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                <PageHeader title={title} />
                { RenderTeam(true, currentData) }
            </CxGrid>
        </Box>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// The end.